import React from "react";
import { formatNumber } from "@danishagro/shared/src/helpers/formatNumber.helper";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { Helmet } from "react-helmet";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { DA_Tooltip } from "@danishagro/shared/src/components/atoms/Tooltip/Tooltip.component";
import { useDynamicOverlay } from "@danishagro/shared/src/hooks/dynamicOverlay/useDynamicOverlay.hook";
import S from "./DiscountTableMobile.module.scss";

interface DiscountRow {
    quantityFrom: number;
    quantityTo?: number;
    price: number;
}

interface DiscountTableProps {
    discountTable: DiscountRow[];
    priceCurrency?: string;
    currentQuantity?: number;
    onRowClick?: (quantity: number) => void;
    clickableRows?: boolean;
    minimumSalesQuantity?: string;
}

export const DiscountTableMobile = ({
    discountTable,
    priceCurrency,
    onRowClick,
    clickableRows,
    currentQuantity,
    minimumSalesQuantity,
}: DiscountTableProps) => {
    const { getDictionaryString } = useTranslations();
    const { currentCulture } = useAppData();
    const { closeOverlay } = useDynamicOverlay();

    const minQtyValue = parseInt(minimumSalesQuantity) || 1;

    const [markedRowQuantity, setMarkedRowQuantity] = React.useState<number | null>(() => {
        if (typeof currentQuantity === "number") {
            // Prefer exact quantityFrom match to handle overlapping boundaries
            const exactMatch = discountTable.find((item) => currentQuantity === item.quantityFrom);
            if (exactMatch) return exactMatch.quantityFrom;
            const matchingRow = discountTable.find(
                (item) =>
                    currentQuantity >= item.quantityFrom &&
                    currentQuantity <= (item.quantityTo || Infinity)
            );
            return matchingRow?.quantityFrom ?? null;
        }
        return null;
    });

    // Find the active row (the row that matches `markedRowQuantity`)
    const activeRowIndex = discountTable.findIndex(
        (item) => markedRowQuantity !== null && item.quantityFrom === markedRowQuantity
    );

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "AggregateOffer",
        offers: discountTable.map((item) => ({
            "@type": "Offer",
            price: formatNumber(item.price, currentCulture, { decimals: 2 }),
            priceCurrency: priceCurrency,
            eligibleQuantity: {
                "@type": "QuantitativeValue",
                value: item.quantityFrom,
            },
        })),
    };

    return (
        <div className={S.discountTable}>
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
            </Helmet>
            <table className={S.discountTable}>
                <tbody>
                    {discountTable.map((item, index) => {
                        const isBelowMin = item.quantityFrom < minQtyValue;
                        return (
                            <tr
                                key={index}
                                className={cn(
                                    index === activeRowIndex && S.activeRow,
                                    isBelowMin && S.disabledRow
                                )}
                                onClick={() => {
                                    if (clickableRows && onRowClick && !isBelowMin) {
                                        setMarkedRowQuantity(item.quantityFrom);
                                    }
                                }}
                            >
                                <td className={S.firstColumn}>
                                    {isBelowMin ? (
                                        <DA_Tooltip
                                            placement="top"
                                            text={getDictionaryString(
                                                "minimumQuantityInfoTextTooltip",
                                                { minQty: minQtyValue }
                                            )}
                                        >
                                            <span className={S.disabledRowContent}>
                                                <span>{item.quantityFrom}</span>
                                                <span>{getDictionaryString("pcs")}</span>
                                                <span className={S.pricePerPcs}>
                                                    {getDictionaryString("discount.PricePerPcs")}
                                                </span>
                                            </span>
                                        </DA_Tooltip>
                                    ) : (
                                        <>
                                            <span>{item.quantityFrom}</span>
                                            <span>{getDictionaryString("pcs")}</span>
                                            <span className={S.pricePerPcs}>
                                                {getDictionaryString("discount.PricePerPcs")}
                                            </span>
                                        </>
                                    )}
                                </td>
                                <td className={cn(S.alignRight, S.secondColumn)}>
                                    {formatNumber(item.price, currentCulture, { decimals: 2 })}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <div className={S.drawerButtonsWrapper}>
                <DA_Button
                    onClick={closeOverlay}
                    isGhost={true}
                    title={getDictionaryString("close")}
                />
                <DA_Button
                    onClick={() => {
                        onRowClick(markedRowQuantity);
                        closeOverlay();
                    }}
                    disabled={!markedRowQuantity}
                    title={
                        !markedRowQuantity
                            ? `${getDictionaryString("amount")}`
                            : ` ${getDictionaryString(
                                  "amount"
                              )}: ${markedRowQuantity} ${getDictionaryString("pcs")}`
                    }
                />
            </div>
        </div>
    );
};
